import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/components/utils/utils";
import type { DashboardFilterOption } from "@/services/dashboard-lists.service";

interface OptionFilterProps {
  options: DashboardFilterOption[];
  /** `value` dell'opzione scelta; null = nessun filtro */
  selected: string | null;
  onChange: (value: string | null) => void;
  /** Etichetta del pulsante quando non c'e' una scelta */
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  disabled?: boolean;
}

/**
 * Menu a tendina con ricerca per filtrare una lista su una persona (consulente o cliente).
 * Le opzioni arrivano dal server: con la paginazione le righe a schermo non bastano a
 * sapere chi c'e' nella tab.
 */
export const OptionFilter = ({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled = false,
}: OptionFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const search = searchTerm.toLowerCase();
  const visibleOptions = options.filter(
    (o) => o.label.toLowerCase().includes(search) || (o.email ?? "").toLowerCase().includes(search)
  );
  const selectedLabel = selected ? (options.find((o) => o.value === selected)?.label ?? selected) : placeholder;

  const handleSelect = (value: string) => {
    onChange(selected === value ? null : value);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="flex items-center gap-2 min-w-[140px] justify-between"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3">
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option) => (
                <button
                  key={option.value}
                  className={cn(
                    "w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between",
                    selected === option.value && "bg-blue-50"
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    {option.email && <span className="text-sm text-gray-500">{option.email}</span>}
                  </div>
                  {selected === option.value && <Check className="h-4 w-4 text-blue-600" />}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">{emptyLabel}</div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
